export type UserPermission = 'MANAGE_PATCH_SCHEDULES' | 'MANAGE_PROJECTS'
export type Role = 'SUPER_ADMIN' | 'ADMIN' | 'MANAGER' | 'MIS_ENGINEER'
export type Me = { email: string; displayName: string; role: Role; permissions: UserPermission[] }
export type AuthOptions = { microsoftLoginEnabled: boolean }
export type ReportEntryType = 'MIS_SUPPORT' | 'PROJECT'

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE'

type CsrfPayload = { token?: string }

function buildApiErrorMessage(status: number, statusText: string, url: string, payload?: unknown): string {
  if (payload && typeof payload === 'object') {
    const detail = 'detail' in payload && typeof payload.detail === 'string' ? payload.detail : ''
    const title = 'title' in payload && typeof payload.title === 'string' ? payload.title : ''

    if (detail) {
      return detail
    }

    if (title) {
      return title
    }
  }

  if (typeof payload === 'string' && payload.trim()) {
    return payload.trim()
  }

  return `HTTP ${status} ${statusText}: ${url}`
}

function readCookie(name: string): string | null {
  const prefix = `${name}=`
  const value = document.cookie
    .split(';')
    .map((item) => item.trim())
    .find((item) => item.startsWith(prefix))

  if (!value) {
    return null
  }

  return decodeURIComponent(value.slice(prefix.length))
}

async function readResponsePayload(response: Response): Promise<unknown> {
  const contentType = response.headers.get('content-type') || ''
  if (contentType.includes('json')) {
    return response.json().catch(() => undefined)
  }

  return response.text().catch(() => '')
}

async function ensureSuccessfulResponse(response: Response, url: string): Promise<Response> {
  if (response.ok) {
    return response
  }

  const payload = await readResponsePayload(response)
  throw new Error(buildApiErrorMessage(response.status, response.statusText, url, payload))
}

async function readJsonResponse<T>(response: Response): Promise<T> {
  if (response.status === 204) {
    return undefined as T
  }

  const contentType = response.headers.get('content-type') || ''
  if (!contentType.includes('json')) {
    return undefined as T
  }

  return (await response.json()) as T
}

let csrfTokenPromise: Promise<string> | null = null

async function initializeCsrfToken(): Promise<string> {
  const existing = readCookie('XSRF-TOKEN')
  if (existing) {
    return existing
  }

  if (!csrfTokenPromise) {
    csrfTokenPromise = fetch('/api/auth/csrf', {
      method: 'GET',
      credentials: 'include'
    })
      .then(async (response) => {
        await ensureSuccessfulResponse(response, '/api/auth/csrf')
        const payload = (await readJsonResponse<CsrfPayload>(response).catch(() => ({}))) as CsrfPayload
        return payload.token || readCookie('XSRF-TOKEN') || ''
      })
      .finally(() => {
        csrfTokenPromise = null
      })
  }

  const token = await csrfTokenPromise
  if (!token) {
    throw new Error('Unable to initialize CSRF token.')
  }

  return token
}

async function buildHeaders(method: HttpMethod, body?: unknown, extra?: Record<string, string>): Promise<Record<string, string> | undefined> {
  const headers: Record<string, string> = { ...(extra ?? {}) }

  if (body !== undefined && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json'
  }

  if (method !== 'GET') {
    headers['X-XSRF-TOKEN'] = await initializeCsrfToken()
  }

  return Object.keys(headers).length ? headers : undefined
}

async function apiRequest<T>(url: string, method: HttpMethod, body?: unknown, opts?: { raw?: boolean }): Promise<T> {
  const response = await fetch(url, {
    method,
    credentials: 'include',
    headers: await buildHeaders(method, body),
    body: body !== undefined ? JSON.stringify(body) : undefined
  })

  await ensureSuccessfulResponse(response, url)

  if (opts?.raw) return (response as unknown) as T
  return readJsonResponse<T>(response)
}

function parseDownloadFileName(contentDisposition: string | null): string | null {
  if (!contentDisposition) {
    return null
  }

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1])
  }

  const quotedMatch = contentDisposition.match(/filename="([^"]+)"/i)
  if (quotedMatch?.[1]) {
    return quotedMatch[1]
  }

  const bareMatch = contentDisposition.match(/filename=([^;]+)/i)
  return bareMatch?.[1]?.trim() ?? null
}

export function apiErrorMessage(error: unknown): string {
  return error instanceof Error && error.message ? error.message : 'Request failed.'
}

export async function apiGet<T>(url: string): Promise<T> { return apiRequest<T>(url, 'GET') }
export async function apiPost<T>(url: string, body?: unknown): Promise<T> { return apiRequest<T>(url, 'POST', body) }
export async function apiPut<T>(url: string, body?: unknown): Promise<T> { return apiRequest<T>(url, 'PUT', body) }
export async function apiDelete<T>(url: string): Promise<T> { return apiRequest<T>(url, 'DELETE') }
export async function apiDownload(url: string): Promise<{ blob: Blob; fileName: string | null }> {
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: await buildHeaders('GET')
  })

  await ensureSuccessfulResponse(response, url)
  return {
    blob: await response.blob(),
    fileName: parseDownloadFileName(response.headers.get('content-disposition'))
  }
}

export async function apiUpload<T>(url: string, file: File, fields?: Record<string, string>): Promise<T> {
  const formData = new FormData()
  formData.append('file', file)
  Object.entries(fields ?? {}).forEach(([key, value]) => formData.append(key, value))

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: await buildHeaders('POST', undefined),
    body: formData
  })

  await ensureSuccessfulResponse(response, url)
  return readJsonResponse<T>(response)
}

export type MisPackage = { id: string; name: string; annualSolutionChangeHours: number; annualPrice: number; hourlyRate?: number | null }
export type DeploymentType = 'ON_PREM' | 'CLOUD'
export type ClusterEnvironment = 'TEST' | 'PROD'
export type PatchScheduleStatus = 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED' | 'ROLLED_BACK'
export type SowStatus = 'DRAFT' | 'SUBMITTED' | 'IN_PROGRESS' | 'UAT' | 'COMPLETED' | 'FAILED' | 'REJECTED' | 'CANCELLED'
export type ForecastStatus = 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'REJECTED' | 'CANCELLED'
export type CustomerMisRenewalType = 'NEW' | 'RENEWAL' | 'EARLY_RENEWAL' | 'UPGRADE'
export type CustomerMisSubscriptionStatus = 'DRAFT' | 'ACTIVE' | 'COMPLETED' | 'CANCELLED'
export type ProjectType = 'MIS_NO_SOW' | 'MIS_SOW' | 'SOW_NON_MIS'
export type ProjectCategory = 'DISCOVERY_DESIGN' | 'IMPLEMENTATION' | 'PROJECT_CHANGE_REQUEST' | 'SOLUTION_CHANGE_REQUEST' | 'STRATEGIC_CONSULTING' | 'TRAINING'
export type CustomerProjectAllowance = { customerId: string; effectiveAllowanceHours: number; allocatedMisNoSowHours: number; remainingMisNoSowHours: number }

export type ImportResult = { dryRun: boolean; processedRows: number; createdCount: number; updatedCount: number; errorCount: number; errors: string[] }

export type NotificationType = 'FORECAST_SUBMITTED' | 'FORECAST_APPROVED' | 'FORECAST_REJECTED' | 'TIME_ENTRY_SUBMITTED' | 'TIME_ENTRY_APPROVED' | 'TIME_ENTRY_REJECTED' | 'IMPORT_COMPLETED'
export type AppNotification = { id: string; type: NotificationType; title: string; body: string; targetUrl?: string | null; read: boolean; createdAt: string }
export type NotificationSummary = { unreadCount: number }
export type ImportAudit = { id: string; entityType: 'CUSTOMER' | 'CLUSTER' | 'PROJECT' | 'TIME_ENTRY'; fileName: string; dryRun: boolean; processedRows: number; createdCount: number; updatedCount: number; errorCount: number; errors: string[]; actorUserId: string; actorEmail: string; createdAt: string }
export type Customer = {
  id: string
  name: string
  misPackageId: string
  misPackageName: string
  misTermStartDate?: string | null
  misTermEndDate?: string | null
  productLicenseStartDate?: string | null
  productLicenseEndDate?: string | null
  allowanceOverrideHours?: number | null
  annualPrice?: number | null
  hourlyRate?: number | null
  deploymentType: DeploymentType
  clusterId?: string | null
  clusterDisplayName?: string | null
  currentProductVersion?: string | null
  active: boolean
}

export type CustomerAuditFieldChange = { field: string; before: string; after: string }
export type CustomerHealthBand = 'HEALTHY' | 'WATCH' | 'AT_RISK'
export type CustomerHealth = {
  customerId: string
  customerName: string
  score?: number | null
  band?: CustomerHealthBand | null
  reasons: string[]
  utilizationPct?: number | null
  daysToRenewal?: number | null
  openSowCount: number
  pendingApprovalCount: number
  scored: boolean
}
export type CustomerHealthConfig = {
  id: string
  utilizationWatchPct: number
  utilizationHighPct: number
  utilizationCriticalPct: number
  utilizationWatchPenalty: number
  utilizationHighPenalty: number
  utilizationCriticalPenalty: number
  renewalWatchDays: number
  renewalHighDays: number
  renewalCriticalDays: number
  renewalWatchPenalty: number
  renewalHighPenalty: number
  renewalCriticalPenalty: number
  openSowWatchCount: number
  openSowHighCount: number
  openSowWatchPenalty: number
  openSowHighPenalty: number
  pendingApprovalWatchCount: number
  pendingApprovalHighCount: number
  pendingApprovalWatchPenalty: number
  pendingApprovalHighPenalty: number
  healthyMinScore: number
  watchMinScore: number
  updatedByUserId?: string | null
  updatedAt: string
}
export type CustomerHealthConfigAuditFieldChange = { field: string; before: string; after: string }
export type CustomerHealthConfigAudit = {
  id: string
  configId: string
  action: 'UPDATE'
  actorUserId: string
  actorEmail: string
  createdAt: string
  summaryText: string
  changes: CustomerHealthConfigAuditFieldChange[]
}

export type CustomerAudit = { id: string; customerId: string; action: 'CREATE' | 'UPDATE' | 'DELETE'; actorUserId: string; actorEmail: string; createdAt: string; summaryText: string; changes: CustomerAuditFieldChange[] }

export type CustomerMisSubscription = {
  id: string
  customerId: string
  customerName: string
  misPackageId: string
  misPackageName: string
  termStartDate: string
  termEndDate: string
  annualHours: number
  annualPrice: number
  hourlyRate?: number | null
  allowanceOverrideHours?: number | null
  renewalType: CustomerMisRenewalType
  status: CustomerMisSubscriptionStatus
  createdAt: string
  updatedAt: string
}

export type CustomerMisSubscriptionAuditFieldChange = { field: string; before: string; after: string }
export type CustomerMisSubscriptionAudit = {
  id: string
  subscriptionId: string
  customerId: string
  action: 'CREATE' | 'UPDATE' | 'DELETE'
  actorUserId: string
  actorEmail: string
  createdAt: string
  summaryText: string
  changes: CustomerMisSubscriptionAuditFieldChange[]
}

export type MisPackageAuditFieldChange = { field: string; before: string; after: string }
export type MisPackageAudit = {
  id: string
  misPackageId: string
  action: 'CREATE' | 'UPDATE' | 'DELETE'
  actorUserId: string
  actorEmail: string
  createdAt: string
  summaryText: string
  changes: MisPackageAuditFieldChange[]
}

export type ForecastAllocation = { id: string; termStartDate: string; termEndDate: string; allocatedHours: number }
export type ForecastProject = {
  id: string
  customerId: string
  customerName: string
  name: string
  description?: string | null
  startDate: string
  endDate: string
  estimatedHours: number
  status: ForecastStatus
  assignedUserId: string
  assignedUserName: string
  reviewerUserId: string
  reviewerUserName: string
  createdByUserId: string
  createdByUserName: string
  allocations: ForecastAllocation[]
  createdAt: string
  updatedAt: string
}
export type ForecastAuditFieldChange = { field: string; before: string; after: string }
export type ForecastAudit = {
  id: string
  forecastProjectId: string
  customerId: string
  action: 'CREATE' | 'UPDATE' | 'SUBMIT' | 'APPROVE' | 'REJECT' | 'CANCEL'
  actorUserId: string
  actorEmail: string
  createdAt: string
  summaryText: string
  changes: ForecastAuditFieldChange[]
}
export type ForecastTermWindow = { termStartDate: string; termEndDate: string; allowanceHours?: number | null; label: string }
export type MisForecastRow = {
  customerId: string
  customerName: string
  misPackageName: string
  termStartDate: string
  termEndDate: string
  allowanceHours: number
  usedHours: number
  remainingHours: number
  committedForecastHours: number
  pendingForecastHours: number
  committedForecastProjectCount: number
  pendingForecastProjectCount: number
  forecastRemainingHours: number
  status: 'ON_TRACK' | 'WATCH' | 'EXCEEDED'
}
export type TeamUtilizationRow = {
  engineerId: string
  engineerName: string
  engineerEmail: string
  supportHours: number
  solutionChangeHours: number
  projectHours: number
  approvedActualHours: number
  pendingEntryHours: number
  committedForecastHours: number
  pendingForecastHours: number
  capacityHours: number
  unusedCapacityHours: number
}
export type ProjectLookup = { id: string; customerId: string; displayName: string; projectType: ProjectType; sowNumber?: string | null; name: string; category: ProjectCategory; active: boolean }
export type ProjectAssignedUser = { id: string; displayName: string; email: string; role: string }
export type Project = {
  id: string
  customerId: string
  customerName: string
  projectType: ProjectType
  sowNumber?: string | null
  name: string
  startDate?: string | null
  endDate?: string | null
  category: ProjectCategory
  budgetedHours: number
  billableRate: number
  active: boolean
  assignedUsers: ProjectAssignedUser[]
  createdByUserEmail: string
  createdAt: string
  updatedAt: string
}
export type ProjectAuditFieldChange = { field: string; before: string; after: string }
export type ProjectAudit = { id: string; projectId: string; customerId: string; action: 'CREATE' | 'UPDATE' | 'DELETE'; actorUserId: string; actorEmail: string; createdAt: string; summaryText: string; changes: ProjectAuditFieldChange[] }
export type ClusterLookup = { id: string; name: string; environment: ClusterEnvironment; displayName: string; currentProductVersion?: string | null; active: boolean }
export type Cluster = { id: string; name: string; environment: ClusterEnvironment; displayName: string; timezoneId: string; timezoneLabel: string; timezoneDisplay: string; currentProductVersion?: string | null; active: boolean; createdAt: string; updatedAt: string }
export type TimezoneOption = { timezoneId: string; timezoneLabel: string; timezoneDisplay: string }
export type AppMeta = { serverTimezoneId: string; serverTimezoneLabel: string; serverTimezoneDisplay: string; portalVersion: string }
export type ReleaseNoteSection = { title: string; bullets: string[] }
export type ReleaseNote = { version: string; releaseDate: string; title: string; summary: string; sections: ReleaseNoteSection[]; current: boolean }
export type ReleaseNotesResponse = PageResult<ReleaseNote>

export type MaintenanceWindow = {
  id: string
  startAt: string
  endAt: string
  clusterIds: string[]
  clusterDisplayNames: string[]
  clusterTimezoneDisplays: string[]
  active: boolean
  createdAt: string
  updatedAt: string
}

export type PatchSchedule = {
  id: string
  name: string
  plannedAt: string
  targetVersion: string
  status: PatchScheduleStatus
  comment?: string | null
  approvedByUserId?: string | null
  completedByUserId?: string | null
  completedAt?: string | null
  clusterIds: string[]
  clusterDisplayNames: string[]
  createdAt: string
  updatedAt: string
}

export type ClusterAuditFieldChange = { field: string; before: string; after: string }
export type ClusterAudit = { id: string; clusterId: string; entityType: 'CLUSTER' | 'MAINTENANCE_WINDOW' | 'PATCH_SCHEDULE'; entityId: string; action: 'CREATE' | 'UPDATE' | 'DELETE'; actorUserId: string; actorEmail: string; createdAt: string; summaryText: string; changes: ClusterAuditFieldChange[] }
export type ClusterVersionHistory = { id: string; clusterId: string; fromVersion?: string | null; toVersion: string; changeType: 'PATCH_COMPLETE' | 'MANUAL_UPDATE' | 'ROLLBACK'; changedAt: string; changedByUserId: string; changedByEmail: string; comment?: string | null }

export type AppUser = { id: string; email: string; displayName: string; role: Role; internalCostRate?: number | null; permissions: UserPermission[]; active: boolean }
export type TimeEntryCategory = 'STANDARD_SUPPORT' | 'SOLUTION_HEALTH_CHECK' | 'SOLUTION_CHANGE' | 'DISCOVERY_DESIGN' | 'IMPLEMENTATION' | 'PROJECT_CHANGE_REQUEST' | 'SOLUTION_CHANGE_REQUEST' | 'STRATEGIC_CONSULTING' | 'TRAINING'
export type TimeEntry = { id: string; workDate: string; customerId: string; customerName: string; category: TimeEntryCategory; projectId?: string | null; projectName?: string | null; durationMinutes: number; zendeskTicket?: string | null; details?: string | null; status: 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'REJECTED'; userId: string; userEmail: string }
export type TimeEntryAuditFieldChange = { field: string; before: string; after: string }
export type TimeEntryAudit = { id: string; timeEntryId: string; customerId: string; userId: string; action: 'CREATE' | 'UPDATE' | 'DELETE' | 'APPROVE' | 'REJECT'; actorUserId: string; actorEmail: string; createdAt: string; summaryText: string; comment?: string | null; changes: TimeEntryAuditFieldChange[] }
export type Balance = { customerId: string; customerName: string; type: ReportEntryType; misPackageName?: string | null; projectId?: string | null; projectName?: string | null; projectCategory?: TimeEntryCategory | null; projectActive?: boolean | null; periodStart: string; periodEnd: string; annualHours?: number | null; allowanceOverrideHours?: number | null; effectiveAllowanceHours?: number | null; usedHours: number; solutionHealthCheckHours?: number | null; strategicAdvisoryHours?: number | null; remainingHours: number; currentPeriod: boolean }
export type BalanceSnapshot = { key: string; customerId: string; asOf: string; balance?: Balance | null }
export type SolutionChangeDetail = { timeEntryId: string; workDate: string; projectId?: string | null; projectName?: string | null; hours: number; ticketNumber?: string | null; details?: string | null; userId: string; userDisplayName: string; userEmail: string; customerId?: string; customerName?: string }
export type TimeEntryReportRow = { timeEntryId: string; workDate: string; customerId: string; customerName: string; type: ReportEntryType; category: TimeEntryCategory; status: 'SUBMITTED' | 'APPROVED' | 'REJECTED'; hours: number; ticketNumber?: string | null; projectId?: string | null; projectName?: string | null; projectActive?: boolean | null; details?: string | null; userId: string; userDisplayName: string; userEmail: string }
export type RevenueReportRow = { rowId: string; rowType: 'SUBSCRIPTION' | 'SOW'; customerId: string; customerName: string; misPackageName?: string | null; subscriptionId?: string | null; subscriptionStatus?: CustomerMisSubscriptionStatus | null; termStartDate?: string | null; termEndDate?: string | null; termYears?: number | null; contractValue?: number | null; realizationStartDate?: string | null; realizationEndDate?: string | null; realizedValue?: number | null; sowId?: string | null; sowName?: string | null; sowStatus?: SowStatus | null; sowStartDate?: string | null; sowEndDate?: string | null; projectedHours?: number | null; hourlyRate?: number | null; budgetAmount?: number | null; actualHours?: number | null; actualAmount?: number | null }
export type InternalRevenueReportRow = { customerId: string; customerName: string; misPackageName?: string | null; misContractValue: number; misRealizedValue: number; misSowRevenue: number; misInternalCosts: number; misBillableInternalCosts: number; sowRevenue: number; sowInternalCosts: number; misMargin: number; misMarginPct: number; sowMargin: number; sowMarginPct: number; totalMargin: number; totalMarginPct: number }
export type DashboardSummary = {
  activeCustomers: number
  activeSubscriptions: number
  renewalsDueSoon: number
  totalEffectiveAllowanceHours: number
  totalUsedHours: number
  totalRemainingHours: number
  activeSowBudgetAmount: number
  activeSowActualAmount: number
  pendingApprovals: number
}
export type DashboardPeriod = 'LAST_30_DAYS' | 'QTD' | 'YTD'
export type DashboardBalanceUtilizationPoint = { customerId: string; customerName: string; usedHours: number; remainingHours: number; utilizationPct: number }
export type DashboardRevenueMix = { misRealizedRevenue: number; sowActualRevenue: number }
export type DashboardStatusCount = { label: string; count: number }
export type DashboardRenewalBucket = { label: string; count: number }
export type DashboardAtRiskCustomer = { customerId: string; customerName: string; score?: number | null; band?: string | null; reasons: string[] }
export type DashboardPendingApproval = { timeEntryId: string; workDate: string; engineerName: string; customerName: string; hours: number }
export type DashboardUpcomingRenewal = { customerId: string; customerName: string; misPackageName: string; termEndDate: string; daysRemaining: number }
export type DashboardVisuals = {
  balanceUtilization: DashboardBalanceUtilizationPoint[]
  revenueMix: DashboardRevenueMix
  projectStatusCounts: DashboardStatusCount[]
  renewalBuckets: DashboardRenewalBucket[]
  atRiskCustomers: DashboardAtRiskCustomer[]
  oldestPendingApprovals: DashboardPendingApproval[]
  upcomingRenewals: DashboardUpcomingRenewal[]
}
export type PageResult<T> = { items: T[]; limit: number; offset: number; hasMore: boolean }

